import { api } from '../core';
import { TIMELINE_TAG } from '../constants';
import {
  RootState,
  setTimelineChronologyData,
  setShowDateBar,
  TimelineChronologyType,
  TimelineChronologyTypeKeys
} from '@/store';

interface TimelineResponse {
  startDate: string | null;
  endDate: string | null;
  fullBundleId?: string | null;
  lightBundleId?: string | null;
  fullBundlePages?: number | null;
  lightBundlePages?: number | null;
  entries: TimelineEntry[];
  entryGroups: TimelineEntryGroup[]; // Add entryGroups like chronology
}

interface TimelineEntryGroup {
  date?: string | null;
  entries: TimelineEntry[];
}

interface TimelineEntry {
  comment: string;
  date: string;
  pages: TimelineEntryPage[];
  providerId: string;
  providerName: string;
  summary: string;
  time: string | null;
  timelineEntryId: string;
}

interface TimelineEntryPage {
  pageId: string;
  fullBundlePageNumber?: number | null;
  lightBundlePageNumber?: number | null;
}

interface GetTimelineProps {
  caseId: string;
  timelineChronologyKey?: TimelineChronologyTypeKeys;
  abortController?: AbortController;
}

const timelineGetApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTimeline: builder.query<TimelineResponse, GetTimelineProps>({
      query: ({ caseId, abortController = new AbortController() }: GetTimelineProps) => ({
        url: `timeline?case_id=${caseId}`,
        method: 'GET',
        signal: abortController.signal
      }),
      providesTags: (_result, _err, { caseId }) => [{ type: TIMELINE_TAG, id: caseId }],
      async onQueryStarted({ timelineChronologyKey }, { dispatch, queryFulfilled, getState }) {
        try {
          const response = await queryFulfilled;
          const state = getState() as RootState;
          const resolvedTimelineChronologyKey = timelineChronologyKey ?? state.sharedData.activeTimelineChronologyType;

          if (response.data && resolvedTimelineChronologyKey === TimelineChronologyType.TIMELINE) {
            // Check if entryGroups exists and is an array
            if (!response.data.entryGroups || !Array.isArray(response.data.entryGroups)) {
              console.warn('timelineGet: entryGroups is missing or not an array:', response.data);
              return;
            }

            let showDateBar = true;
            const data = {
              startDate: response.data.startDate,
              endDate: response.data.endDate,
              fullBundlePages: response.data.fullBundlePages,
              lightBundlePages: response.data.lightBundlePages,
              fullBundleId: response.data.fullBundleId,
              lightBundleId: response.data.lightBundleId,
              entries: response.data.entryGroups.flatMap((entryGroup: TimelineEntryGroup) => {
                const groupDate = entryGroup.date;
                if (groupDate) showDateBar = false;
                const entries = entryGroup.entries || [];

                const mappedEntries = entries.map((entry: TimelineEntry) => ({
                  id: entry.timelineEntryId,
                  date: entry.date,
                  providerId: entry.providerId,
                  providerName: entry.providerName,
                  summary: entry.summary,
                  comment: entry.comment,
                  pages: entry.pages,
                  time: entry.time
                }));
                return [groupDate, ...mappedEntries];
              })
            };

            dispatch(
              setTimelineChronologyData({
                key: resolvedTimelineChronologyKey,
                data
              })
            );
            dispatch(
              setShowDateBar({
                key: resolvedTimelineChronologyKey,
                data: showDateBar
              })
            );
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getTimeline in onQueryStarted:', error);
          }
        }
      }
    })
  })
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetTimelineQuery } = timelineGetApi;
